/**
 * Migrate Numeric DependsOn Migration
 * 
 * Legacy campaigns stored `dependsOn` as numeric array indices
 * (e.g. [0, 3]) meaning "depends on task at position 0 and 3".
 * Modern format uses string task IDs.
 * 
 * This migration converts numeric entries to actual task ID strings,
 * which fixes dependency resolution across all views (Flow, Neural, Gantt, etc.).
 * 
 * Only runs when a sprint has tasks with numeric dependsOn entries.
 * Safe to run multiple times — already-migrated tasks are skipped.
 */

/**
 * Check if a sprint has any tasks with numeric dependsOn entries
 */
export function needsNumericDepsMigration(sprint) {
  if (!sprint?.tasks || sprint.tasks.length === 0) return false;
  return sprint.tasks.some((task) => {
    if (!Array.isArray(task.dependsOn)) return false;
    return task.dependsOn.some((dep) => typeof dep === 'number');
  });
}

/**
 * Migrate numeric dependsOn entries to task ID strings.
 * 
 * @param {object} sprint - Sprint object with tasks array
 * @returns {{ sprint: object, migrated: boolean, migratedCount: number }}
 */
export function migrateNumericDependsOn(sprint) {
  if (!needsNumericDepsMigration(sprint)) {
    return { sprint, migrated: false, migratedCount: 0 };
  }

  const tasks = sprint.tasks;
  let migratedCount = 0;

  const migratedTasks = tasks.map((task) => {
    if (!Array.isArray(task.dependsOn)) return task;

    const hasNumeric = task.dependsOn.some((dep) => typeof dep === 'number');
    if (!hasNumeric) return task;

    const newDepsOn = task.dependsOn
      .map((dep) => {
        if (typeof dep === 'number') {
          const target = tasks[dep];
          if (target?.id) return target.id;
          return null;
        }
        if (typeof dep === 'object' && dep !== null && typeof dep.task === 'number') {
          const target = tasks[dep.task];
          if (!target?.id) return null;
          if (dep.branch !== undefined) {
            return { taskId: target.id, branch: dep.branch };
          }
          return target.id;
        }
        return dep;
      })
      .filter(Boolean);

    migratedCount++;
    return { ...task, dependsOn: newDepsOn };
  });

  console.log(`🔗 Migrated numeric dependsOn for ${migratedCount} tasks in: ${sprint.name || sprint.id}`);

  return {
    sprint: { ...sprint, tasks: migratedTasks },
    migrated: true,
    migratedCount,
  };
}
